"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SceneManager = exports.Layer = exports.Scenes = void 0;
const rxjs_1 = require("rxjs");
const HomeMenuScene_1 = require("../scenes/HomeMenuScene");
const ActionRoomScene_1 = require("../scenes/ActionRoomScene");
const SpeedRoomScene_1 = require("../scenes/SpeedRoomScene");
const PracticeRoomScene_1 = require("../scenes/PracticeRoomScene");
const StatsMenuScene_1 = require("../scenes/StatsMenuScene");
const RoundScene_1 = require("../scenes/RoundScene");
const DrawActionScene_1 = require("../scenes/DrawActionScene");
const PromiseChain_1 = require("../../../shared/PromiseChain");
const CompleteScene_1 = require("../scenes/CompleteScene");
const PrivateRoomScene_1 = require("../scenes/PrivateRoomScene");
const LogoScene_1 = require("../scenes/LogoScene");
const AdManager_1 = require("./AdManager");
const OtherModesScene_1 = require("../scenes/OtherModesScene");
const StoreMenuScene_1 = require("../scenes/StoreMenuScene");
const TutorialRoomScene_1 = require("../scenes/TutorialRoomScene");
const GuessActionScene_1 = require("../scenes/GuessActionScene");
const LevelsRoomScene_1 = require("../scenes/LevelsRoomScene");
const interfaces_1 = require("../../../shared/interfaces");
var Scenes;
(function (Scenes) {
    Scenes["Home"] = "home";
    Scenes["Battle"] = "battle";
    Scenes["PrivateBattle"] = "privatebattle";
    Scenes["PrivateGuess"] = "privateguess";
    Scenes["Speed"] = "speed";
    Scenes["Stats"] = "stats";
    Scenes["Practice"] = "practice";
    Scenes["Round"] = "round";
    Scenes["Draw"] = "draw";
    Scenes["Complete"] = "complete";
    Scenes["Logo"] = "logo";
    Scenes["Others"] = "others";
    Scenes["Atelier"] = "atelier";
    Scenes["Tutorial"] = "tutorial";
    Scenes["Guess"] = "guess";
    Scenes["Guesser"] = "guesser";
    Scenes["Levels"] = "levels";
})(Scenes || (exports.Scenes = Scenes = {}));
var Layer;
(function (Layer) {
    Layer[Layer["Popup"] = 2000] = "Popup";
    Layer[Layer["Header"] = 1000] = "Header";
    Layer[Layer["Top"] = 200] = "Top";
    Layer[Layer["Middle"] = 100] = "Middle";
    Layer[Layer["Bottom"] = 2] = "Bottom";
    Layer[Layer["Menu"] = 1] = "Menu";
})(Layer || (exports.Layer = Layer = {}));
class SceneManager {
    constructor(app, gameManager) {
        this.app = app;
        this.gameManager = gameManager;
        this.chain = Promise.resolve();
        this._isRouting = false;
        this.sceneWillChange$ = new rxjs_1.Subject();
        this.scene = new rxjs_1.BehaviorSubject(null);
        this.sceneDidChange$ = this.scene.asObservable();
    }
    get currentScene() {
        return this.scene.value;
    }
    get isRouting() {
        return this._isRouting;
    }
    routeTo(scene = Scenes.Home, target = null, force = false) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.series(() => __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (!force && scene === ((_a = this.scene.value) === null || _a === void 0 ? void 0 : _a.name)) {
                    return false;
                }
                this._isRouting = true;
                let next;
                switch (scene) {
                    case Scenes.Battle:
                        next = new ActionRoomScene_1.ActionRoomScene(this.app, this, this.gameManager, interfaces_1.GameType.Battle);
                        break;
                    case Scenes.Guess:
                        next = new ActionRoomScene_1.ActionRoomScene(this.app, this, this.gameManager, interfaces_1.GameType.Guess);
                        break;
                    case Scenes.PrivateBattle:
                        next = new PrivateRoomScene_1.PrivateRoomScene(this.app, this, this.gameManager, interfaces_1.GameType.BattleFriends);
                        break;
                    case Scenes.PrivateGuess:
                        next = new PrivateRoomScene_1.PrivateRoomScene(this.app, this, this.gameManager, interfaces_1.GameType.GuessFriends);
                        break;
                    case Scenes.Speed:
                        next = new SpeedRoomScene_1.SpeedRoomScene(this.app, this, this.gameManager);
                        break;
                    case Scenes.Practice:
                        next = new PracticeRoomScene_1.PracticeRoomScene(this.app, this, this.gameManager);
                        break;
                    case Scenes.Stats:
                        next = new StatsMenuScene_1.StatsMenuScene(this.app, this, this.gameManager);
                        break;
                    case Scenes.Round:
                        next = new RoundScene_1.RoundScene(this.app, this, this.gameManager);
                        break;
                    case Scenes.Draw:
                        next = new DrawActionScene_1.DrawActionScene(this.app, this, this.gameManager);
                        break;
                    case Scenes.Complete:
                        next = new CompleteScene_1.CompleteScene(this.app, this, this.gameManager);
                        break;
                    case Scenes.Logo:
                        next = new LogoScene_1.LogoScene(this.app, this, this.gameManager);
                        break;
                    case Scenes.Others:
                        next = new OtherModesScene_1.OtherModesScene(this.app, this, this.gameManager);
                        break;
                    case Scenes.Atelier:
                        next = new StoreMenuScene_1.StoreMenuScene(this.app, this, this.gameManager);
                        break;
                    case Scenes.Tutorial:
                        next = new TutorialRoomScene_1.TutorialRoomScene(this.app, this, this.gameManager);
                        break;
                    case Scenes.Guesser:
                        next = new GuessActionScene_1.GuessActionScene(this.app, this, this.gameManager);
                        break;
                    case Scenes.Levels:
                        next = new LevelsRoomScene_1.LevelsRoomScene(this.app, this, this.gameManager);
                        break;
                    case Scenes.Home:
                    default:
                        next = new HomeMenuScene_1.HomeMenuScene(this.app, this, this.gameManager);
                        break;
                }
                // Switch scenes
                const cur = this.scene.value;
                if (cur !== null) {
                    yield cur.deinit();
                    cur.zIndex = Layer.Bottom;
                }
                if (!next.zIndex) {
                    next.zIndex = Layer.Middle;
                }
                next.name = scene || Scenes.Home;
                this.app.stage.addChild(next);
                this.sceneWillChange$.next(next);
                if (force || (yield next.enterScene(cur, target))) {
                    yield this.triggerTriggers(next);
                    if (force || !cur || (yield cur.exitScene(next, target))) {
                        if (cur) {
                            try {
                                cur.destroy({
                                    children: true
                                });
                            }
                            catch (e) {
                                // Sometimes we get strange detached events, I think it's a bug in the textfield class
                                console.log(e);
                            }
                        }
                        this._isRouting = false;
                        this.scene.next(next);
                        yield next.init();
                        return true;
                    }
                    this._isRouting = false;
                    return false;
                }
                else {
                    this.app.stage.removeChild(next);
                    this._isRouting = false;
                    return false;
                }
            }));
        });
    }
    triggerTriggers(cur) {
        return __awaiter(this, void 0, void 0, function* () {
            // Poki Break
            if (cur.triggersStop) {
                this.gameManager.stopPlay();
            }
            else if (cur.triggersStart) {
                if (!this.gameManager.isPlaying) {
                    yield this.gameManager.adManager.triggerInterstitial(AdManager_1.AdUnit.GameplayStart);
                    this.gameManager.startPlay();
                }
            }
        });
    }
    series(func) {
        return (0, PromiseChain_1.series)(func, this);
    }
}
exports.SceneManager = SceneManager;
